import SparkMD5 from "spark-md5";

export interface Md5Options {
  /** Bytes read per slice. Small = lower peak memory. Never affects the digest. */
  readChunkSize?: number;
}

const DEFAULT_READ_CHUNK = 8 * 1024 * 1024; // 8 MB

type FileReaderSyncLike = { readAsArrayBuffer(blob: Blob): ArrayBuffer };

/**
 * Read one slice as an ArrayBuffer, in order of preference:
 *
 *  1. `blob.arrayBuffer()` — present everywhere modern, works on both the main
 *     thread and inside a Worker.
 *  2. `FileReaderSync` — Worker-only API, the correct fallback for older
 *     WebKit builds where Blob.arrayBuffer is missing.
 *  3. `FileReader` — main-thread fallback for the same old builds.
 *
 * Every global is reached through `globalThis` on purpose. A bare `FileReader`
 * reference throws ReferenceError ("Can't find variable: FileReader") in the
 * environments that lack it — several in-app WebViews and Worker scopes do —
 * and that ReferenceError escaped as an unhandled rejection instead of letting
 * the caller fall back. Property access on globalThis is just `undefined`.
 */
async function readSliceAsArrayBuffer(slice: Blob): Promise<ArrayBuffer> {
  if (typeof slice.arrayBuffer === "function") {
    return slice.arrayBuffer();
  }

  const g = globalThis as typeof globalThis & {
    FileReaderSync?: new () => FileReaderSyncLike;
    FileReader?: typeof FileReader;
  };

  if (typeof g.FileReaderSync === "function") {
    return new g.FileReaderSync().readAsArrayBuffer(slice);
  }

  const AsyncReader = g.FileReader;
  if (typeof AsyncReader === "function") {
    return new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new AsyncReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      // Rejects with a real Error (not the ProgressEvent), so callers logging
      // `error.message` get something readable instead of "[object Object]".
      reader.onerror = () =>
        reject(new Error("Error reading the file (FileReader)"));
      try {
        reader.readAsArrayBuffer(slice);
      } catch (err) {
        reject(err instanceof Error ? err : new Error("readAsArrayBuffer threw"));
      }
    });
  }

  throw new Error("No Blob reader available in this environment");
}

/**
 * MD5 (lowercase hex) of a file, matching the single-part S3/MinIO ETag.
 * Reads the file in slices and hashes incrementally to keep peak memory low;
 * the digest is identical regardless of `readChunkSize`. Empty file -> the MD5
 * of empty input (d41d8cd98f00b204e9800998ecf8427e).
 *
 * The presigned upload path always issues single PUTs, so MinIO always returns
 * a single-part ETag — there is no multipart-ETag case to reproduce.
 */
export async function computeMd5FromFile(
  file: Blob,
  opts: Md5Options = {}
): Promise<string> {
  const readChunkSize = opts.readChunkSize ?? DEFAULT_READ_CHUNK;
  const spark = new SparkMD5.ArrayBuffer();
  for (let offset = 0; offset < file.size; offset += readChunkSize) {
    const slice = file.slice(offset, Math.min(offset + readChunkSize, file.size));
    spark.append(await readSliceAsArrayBuffer(slice));
  }
  return spark.end();
}
